import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { skipToken } from '@tanstack/react-query';
import { captureException, fetchAllData } from 'r/utils';
import { fetchApiRaw, GET_ISSUERS, GET_ANALYSES } from 'r/utils/fetchApiRaw';
import type { IssuerWithPerformanceMetrics, Analyse } from 'r/types';
import { filtredAnalysisAuthorIds } from 'r/constants/authors';
import {
  buildFilterString,
  serializeSort,
  type FilterCondition,
} from './utils';
import { issuersKolsAtom } from './kols';

export const adminFiltersAtom = atom({
  author: null as string | null,
  symbol: null as string | null,
  search: '',
  showApiAnalysesOnly: false,
  page: 0,
  limit: 50,
  timeframe: null as string | null,
  performanceFilter: null as string | null,
});

export const adminHistoryAuthorsQueryAtom = atomWithQuery(() => ({
  queryKey: ['admin-kol-authors'],
  queryFn: async (): Promise<IssuerWithPerformanceMetrics[]> => {
    try {
      const response = await fetchAllData(GET_ISSUERS, {
        queryParams: {
          filter: '"adminInfo.isKol":true',
          take: '500',
        },
      });

      return Array.isArray(response.data) ?
          (response.data as IssuerWithPerformanceMetrics[])
        : [];
    } catch (e) {
      captureException(e);
      return [];
    }
  },
  gcTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
}));

export const adminAnalysesQueryAtom = atomWithQuery((get) => {
  const filters = get(adminFiltersAtom);
  const authorsData = get(adminHistoryAuthorsQueryAtom).data || [];
  const kols = get(issuersKolsAtom).data;

  return {
    queryKey: [
      'admin-analyses',
      filters.author,
      filters.symbol,
      filters.search,
      filters.showApiAnalysesOnly,
      filters.page,
      filters.limit,
      filters.timeframe,
      filters.performanceFilter,
      kols?.map((x) => x.twitterID).join(),
    ],
    queryFn:
      !kols ? skipToken : (
        async () => {
          try {
            if (authorsData.length === 0) {
              return { data: [], total: 0 };
            }

            let authorFilterUsernames: string[] = [];

            if (filters.author) {
              authorFilterUsernames = [filters.author];
            } else {
              authorFilterUsernames = authorsData
                .map((author) => author.username)
                .filter((username): username is string => !!username);
            }

            if (authorFilterUsernames.length === 0) {
              return { data: [], total: 0 };
            }

            const filterConditions: Record<string, FilterCondition> = {};

            filterConditions.authorID = kols
              .filter((x) => authorFilterUsernames.includes(x.username))
              .map((x) => x.twitterID);

            if (
              filters.showApiAnalysesOnly &&
              filtredAnalysisAuthorIds.length > 0
            ) {
              filterConditions.analysisAuthorId = filtredAnalysisAuthorIds[0];
            }

            if (filters.symbol) {
              filterConditions.pair = filters.symbol;
            }

            if (filters.search.trim()) {
              const searchPattern = `%${filters.search.trim()}%`;
              filterConditions.$or = [
                { tweetText: { $ilike: searchPattern } },
                { authorName: { $ilike: searchPattern } },
                { authorUsername: { $ilike: searchPattern } },
              ];
            }

            if (filters.timeframe && filters.performanceFilter) {
              const perfField = `performance_${filters.timeframe}`;
              const isGreater = filters.performanceFilter.startsWith('>');
              const threshold = String(
                parseFloat(filters.performanceFilter.slice(1)) / 100,
              );
              filterConditions[perfField] =
                isGreater ? { $gt: threshold } : { $lt: threshold };
            }

            const queryParams: Record<string, string> = {
              skip: String(filters.page * filters.limit),
              take: String(filters.limit),
            };
            queryParams.sort = serializeSort('tweetDate', 'DESC');

            if (Object.keys(filterConditions).length > 0) {
              queryParams.filter = buildFilterString(filterConditions);
            }

            const response = await fetchApiRaw(GET_ANALYSES, { queryParams });
            return {
              data:
                Array.isArray(response.data) ?
                  (response.data as Analyse[])
                : [],
              total: response.total || 0,
            };
          } catch (e) {
            captureException(e);
            return { data: [], total: 0 };
          }
        }
      ),
    enabled: authorsData.length > 0,
    keepPreviousData: true,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  };
});

export const adminFilterOptionsQueryAtom = atom((get) => {
  const filters = get(adminFiltersAtom);
  const authorsData = get(adminHistoryAuthorsQueryAtom).data || [];

  try {
    let filteredAuthors = authorsData;

    if (filters.symbol) {
      filteredAuthors = authorsData.filter(
        (author) =>
          author.topTokens && author.topTokens[filters.symbol!] !== undefined,
      );
    }

    if (filters.search.trim()) {
      const searchLower = filters.search.trim().toLowerCase();
      filteredAuthors = filteredAuthors.filter(
        (author) =>
          author.username?.toLowerCase().includes(searchLower) ||
          author.twitter?.toLowerCase().includes(searchLower),
      );
    }

    const authors = filteredAuthors
      .map((author) => ({
        value: author.username || '',
        count: author.predictionsCount || 0,
      }))
      .filter((author) => author.value)
      .sort((a, b) => a.value.localeCompare(b.value));

    const symbolMap = new Map<string, number>();

    if (filters.author) {
      const selectedAuthor = authorsData.find(
        (a) => a.username === filters.author,
      );
      if (selectedAuthor?.topTokens) {
        Object.entries(selectedAuthor.topTokens).forEach(([symbol, count]) => {
          symbolMap.set(symbol, count);
        });
      }
    } else {
      filteredAuthors.forEach((author) => {
        if (author.topTokens) {
          Object.entries(author.topTokens).forEach(([symbol, count]) => {
            const current = symbolMap.get(symbol) || 0;
            symbolMap.set(symbol, current + count);
          });
        }
      });
    }

    const symbols = Array.from(symbolMap.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value));

    return {
      authors,
      symbols,
    };
  } catch (e) {
    captureException(e);
    return { authors: [], symbols: [] };
  }
});
